import { css, cva } from '@styled-system/css';
import { Bell, ChartLine, ExternalLink, Pause, Play, Radar, Radio, SlidersHorizontal, Trash2 } from 'lucide-react';
import { type FC, type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { SearchModal } from '@/features/productSearch/components/SearchModal';
import { AnalysisSimilarityDegradedChip } from '@/shared/components/analysis/AnalysisChips';
import { ProductSalesFeed } from '@/shared/components/analysis/ProductSalesFeed';
import { AppLayout } from '@/shared/components/layout/AppLayout';
import { Button } from '@/shared/components/ui/Button';
import { Message } from '@/shared/components/ui/Message';
import { Spinner } from '@/shared/components/ui/Spinner';

import { useVeilleDetail, useVeilleFeed, useVeilleLifecycle } from '../useVeilleApi';
import { VeilleFeedScanning, VeilleFeedSkeleton, VeilleHistoryPending } from './VeilleFeedSkeleton';
import { VeilleLiveChart } from './VeilleLiveChart';
import { VeilleNotifications } from './VeilleNotifications';
import { VeilleStats } from './VeilleStats';
import { liveBadge, liveDot, pausedBadge } from './veilleUi';

// ============================================================================
// Détail d'une veille (API réelle) : Ventes en direct / Statistiques /
// Notifications. Le direct (feed + courbe) reste lisible en pause et pendant
// le backfill — c'est le cumul nocturne qui s'arrête, pas le monde.
// ============================================================================

const page = css({ display: 'flex', flexDirection: 'column', gap: '6', width: '100%' });

const tabBar = css({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '1',
  padding: '2',
  bg: 'surface.subtle',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'border.default',
  borderRadius: 'xl',
  width: 'fit-content',
});

const tabButton = cva({
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '3',
    paddingY: '3',
    paddingX: '7',
    fontSize: 'body',
    fontFamily: 'sans',
    fontWeight: 'medium',
    borderRadius: 'lg',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'transparent',
    background: 'transparent',
    color: 'text.secondary',
    cursor: 'pointer',
    transition: 'background 120ms ease, color 120ms ease, box-shadow 120ms ease',
    whiteSpace: 'nowrap',
  },
  variants: {
    active: {
      true: {
        bg: 'surface.default',
        color: 'text.primary',
        fontWeight: 'semibold',
        borderColor: 'border.default',
        boxShadow: 'xs',
      },
      false: { _hover: { color: 'text.primary' } },
    },
  },
});

const headerThumb = css({
  width: '32px',
  height: '32px',
  borderRadius: 'md',
  objectFit: 'cover',
  bg: 'surface.sunken',
  flexShrink: 0,
});

const headerThumbFallback = css({
  width: '32px',
  height: '32px',
  borderRadius: 'md',
  bg: 'surface.sunken',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'text.tertiary',
  flexShrink: 0,
});

type VeilleTab = 'live' | 'stats' | 'notifications';

export const VeilleDetailPage: FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { veille, isLoading, notFound } = useVeilleDetail(id);
  const { pause, resume, stop, rename } = useVeilleLifecycle();
  const [activeTab, setActiveTab] = useState<VeilleTab>('live');
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  const feed = useVeilleFeed(id, activeTab === 'live');

  const formatDate = (value: string): string =>
    new Date(value).toLocaleDateString(i18n.language?.startsWith('en') ? 'en-GB' : 'fr-FR');

  const tabs: { value: VeilleTab; label: string; icon: ReactNode }[] = [
    { value: 'live', label: t('veille.tabLive'), icon: <Radio size={15} strokeWidth={1.75} /> },
    { value: 'stats', label: t('veille.tabStats'), icon: <ChartLine size={15} strokeWidth={1.75} /> },
    { value: 'notifications', label: t('veille.tabNotifications'), icon: <Bell size={15} strokeWidth={1.75} /> },
  ];

  if (isLoading) {
    return (
      <AppLayout title={t('veille.title')} breadcrumbs={[{ title: t('veille.title'), onClick: () => navigate('/veille') }]}>
        <div className={css({ display: 'flex', justifyContent: 'center', paddingY: '20' })}>
          <Spinner size={24} />
        </div>
      </AppLayout>
    );
  }

  if (notFound || !veille) {
    return (
      <AppLayout title={t('veille.title')} breadcrumbs={[{ title: t('veille.title'), onClick: () => navigate('/veille') }]}>
        <div className={css({ display: 'flex', justifyContent: 'center', paddingY: '20' })}>
          <Message type="error" bordered>
            {t('veille.notFound')}
          </Message>
        </div>
      </AppLayout>
    );
  }

  const isPaused = veille.state === 'paused';
  const historyPending = veille.historyState === 'queued' || veille.historyState === 'running';

  return (
    <AppLayout
      title={veille.name}
      breadcrumbs={[{ title: t('veille.title'), onClick: () => navigate('/veille') }]}
      headerLeading={
        veille.imageUrl ? (
          <img src={veille.imageUrl} alt="" className={headerThumb} />
        ) : (
          <span className={headerThumbFallback}>
            <Radar size={14} strokeWidth={1.75} />
          </span>
        )
      }
      headerTitleExtras={
        veille.pageUrl ? (
          <a
            href={veille.pageUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={css({ display: 'inline-flex', color: 'text.tertiary', _hover: { color: 'brand.primary' } })}
            aria-label={t('analysisDetail.openOnVinted')}
          >
            <ExternalLink size={15} strokeWidth={1.75} />
          </a>
        ) : undefined
      }
      headerStats={
        <>
          {isPaused ? (
            <span className={pausedBadge}>
              <Pause size={11} strokeWidth={2} />
              {t('veille.pausedBadge')}
            </span>
          ) : (
            <span className={liveBadge}>
              <span className={liveDot} />
              {t('veille.liveBadge')}
            </span>
          )}
          <AnalysisSimilarityDegradedChip degraded={veille.similarityDegraded} />
          <span>{veille.type === 'product' ? t('veille.typeProduct') : t('veille.typeVendor')}</span>
          <span>·</span>
          <span>{t('veille.watchedSince', { date: formatDate(veille.createdAt) })}</span>
        </>
      }
      headerActions={
        <>
          {/* Critères de la veille (produit) : popup en lecture seule, le nom
              reste éditable (renameVeille) */}
          {veille.type === 'product' && (
            <Button
              variant="ghost"
              size="sm"
              icon={<SlidersHorizontal size={13} strokeWidth={1.75} />}
              onClick={() => setIsFiltersOpen(true)}
            >
              {t('veille.viewFilters')}
            </Button>
          )}
          {veille.sourceAnalysisId && (
            <Button
              variant="ghost"
              size="sm"
              icon={<ExternalLink size={13} strokeWidth={1.75} />}
              onClick={() =>
                navigate(`/analysis/${veille.type === 'vendor' ? 'vendors' : 'products'}/${veille.sourceAnalysisId}`)
              }
            >
              {t('veille.openSource')}
            </Button>
          )}
          <Button
            variant="secondary"
            size="sm"
            icon={isPaused ? <Play size={13} strokeWidth={1.75} /> : <Pause size={13} strokeWidth={1.75} />}
            handleClick={async () => {
              await (isPaused ? resume(veille.id) : pause(veille.id));
            }}
            showLoader
          >
            {isPaused ? t('veille.resume') : t('veille.pause')}
          </Button>
          <Button
            variant="danger"
            size="sm"
            icon={<Trash2 size={13} strokeWidth={1.75} />}
            handleClick={async () => {
              if (window.confirm(t('veille.deleteConfirm', { name: veille.name }))) {
                await stop(veille.id);
                navigate('/veille');
              }
            }}
            showLoader
          >
            {t('veille.stop')}
          </Button>
        </>
      }
    >
      <div className={page}>
        <div className={tabBar}>
          {tabs.map((tab) => (
            <button
              key={tab.value}
              type="button"
              className={tabButton({ active: activeTab === tab.value })}
              onClick={() => setActiveTab(tab.value)}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        <div className={css({ height: '1px', bg: 'border.default' })} />

        <div className={css({ paddingTop: '2' })}>
          {activeTab === 'live' && (
            <div className={css({ display: 'flex', flexDirection: 'column', gap: '5' })}>
              {isPaused && (
                <span className={css({ fontSize: 'caption', color: 'text.tertiary' })}>{t('veille.pausedHint')}</span>
              )}
              <VeilleLiveChart veille={veille} />
              {/* Premier chargement (lecture bornée à 12 s côté serveur) :
                  squelettes explicatifs. Balayage par tranches (pages vides
                  avec curseur) : état dédié, jamais un faux « aucune vente ». */}
              {feed.isLoading ? (
                <VeilleFeedSkeleton />
              ) : feed.products.length === 0 && (feed.isScanning || feed.isFetchingNextPage) ? (
                <VeilleFeedScanning />
              ) : (
                <ProductSalesFeed
                  products={feed.products}
                  onEndReached={feed.hasNextPage && !feed.isFetchingNextPage ? () => feed.fetchNextPage() : undefined}
                  loadingMore={feed.isFetchingNextPage}
                  emptyTitle={t('veille.liveEmpty')}
                  emptyHint={t('veille.liveEmptyHint')}
                />
              )}
            </div>
          )}
          {activeTab === 'stats' &&
            (historyPending ? (
              // Backfill en cours : rien à tracer encore — gros état d'attente
              // explicite plutôt que des graphes vides
              <VeilleHistoryPending onGoLive={() => setActiveTab('live')} />
            ) : (
              <div className={css({ display: 'flex', flexDirection: 'column', gap: '4' })}>
                {veille.historyState === 'failed' && (
                  <Message type="error" bordered>
                    {t('veille.historyFailedHint')}
                  </Message>
                )}
                <VeilleStats veille={veille} />
              </div>
            ))}
          {activeTab === 'notifications' && <VeilleNotifications veille={veille} />}
        </div>
      </div>

      {/* Critères de la veille — lecture seule, nom éditable */}
      {veille.type === 'product' && (
        <SearchModal
          isOpen={isFiltersOpen}
          mode="edit"
          variant="veille"
          initialName={veille.name}
          initialUrl={veille.pageUrl ?? undefined}
          initialParsedFilter={veille.parsedFilter}
          initialImageUrl={veille.imageUrl}
          onClose={() => setIsFiltersOpen(false)}
          onSubmit={async ({ title }) => {
            await rename(veille.id, title);
          }}
        />
      )}
    </AppLayout>
  );
};
